"use client";

import React, { useEffect, useState } from "react";
import {
  Box,
  Spinner,
  Heading,
  useTheme,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const Chart10 = ({ supplier, retailer }) => {
  const theme = useTheme();
  const [divRef, setDivRef] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let root;

    const build = async () => {
      if (!divRef) return;
      setLoading(true);

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/rep_sales/chart10`,
          { supplier, retailer, userid: localStorage.getItem("userid") }
        );
        const data = res.data.data; // [{ sku, value, segment }]
        // console.log(data) 
        // Dynamically size container so each row ~40px tall
        const height = Math.max(data.length * 40, 300);
        divRef.style.height = `${height}px`;

        // Dispose previous chart if any
        if (root) {
          root.dispose();
        }
        root = am5.Root.new(divRef);
        root.setThemes([am5themes_Animated.new(root)]);
        root.numberFormatter.set("numberFormat", "#,###");

        // Create the XY chart
        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            layout: root.verticalLayout,
            paddingLeft: 0,    // no extra gap on left
            paddingRight: 20,
            paddingBottom: 20,
          })
        );

        //
        // Y-AXIS (SKU labels)
        //
        const yRenderer = am5xy.AxisRendererY.new(root, { inversed: true });
        // ensure each category row fills the full 40px we gave it
        yRenderer.setAll({
          cellStartLocation: 0,
          cellEndLocation:   1,
          minGridDistance:   0   // 🔑 allow labels closer than default
        });
        // hide grid lines
        yRenderer.grid.template.set("visible", false);
        // styling for axis labels (one-line, ellipsis, flush right)
        yRenderer.labels.template.setAll({
          visible: true,
          fontSize: 10,
          maxWidth: 200,
          wrap: false,
          ellipsis: true,
          textAlign: "end",
          dx: -8,
          centerY: am5.p50,
          fill: am5.color("#9ca3af"),
          tooltipText: "{category}",
        });

        const yAxis = chart.yAxes.push(
          am5xy.CategoryAxis.new(root, {
            categoryField: "sku",
            renderer:        yRenderer
          })
        );
        yAxis.data.setAll(data);

        //
        // X-AXIS (values)
        //
        const xAxis = chart.xAxes.push(
          am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererX.new(root, {
              strokeOpacity: 0.1
            })
          })
        );
        xAxis.get("renderer").grid.template.setAll({
          strokeOpacity: 0.15,
          strokeDasharray: [3, 3],
          stroke: am5.color("#e5e7eb"),
        });
        xAxis.get("renderer").labels.template.setAll({
          fontSize: 10,
          fill: am5.color("#9ca3af"),
        });
        //
        // SERIES (bars)
        //
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name:           "SKU Sales",
            xAxis,
            yAxis,
            valueXField:    "value",
            categoryYField: "sku",
            sequencedInterpolation: true,
            locationX:      0      // width % measured from 0→
          })
        );
        series.data.setAll(data);
        // make bars only 15% wide
        const tooltip = am5.Tooltip.new(root, {
          getFillFromSprite: false,
          labelText: "{categoryY}: R{valueX.formatNumber('#,###')}",
        });
        tooltip.get("background").setAll({
          fill: am5.color("#ffffff"),
          fillOpacity: 0.95,
          stroke: am5.color("#e5e7eb"),
          strokeWidth: 1,
          shadowColor: am5.color("#000000"),
          shadowBlur: 8,
          shadowOffsetX: 0,
          shadowOffsetY: 2,
          shadowOpacity: 0.08,
          cornerRadius: 6,
        });
        tooltip.label.setAll({
          fill: am5.color("#374151"),
          fontSize: 12,
        });

        series.columns.template.setAll({
          cornerRadiusTR: 5,
          cornerRadiusBR: 5,
          strokeOpacity: 1,
          strokeWidth: 1,
          width: am5.percent(15),
          stroke: am5.color("#FFFFFF"),
          tooltip: tooltip,
          tooltipText: "{categoryY}: R{valueX.formatNumber('#,###')}",
        });
        // color by segment
        series.columns.template.adapters.add("fill", (fill, target) => {
          const seg = target.dataItem.dataContext.segment;
          let hex;
          if (seg === "A") {
            hex = theme.colors.green["500"];
          } else if (seg === "B") {
            hex = theme.colors.orange["400"];
          } else {
            hex = theme.colors.red["500"];
          }
          return am5.color(hex);
        });

        chart.appear(800, 400);
        setLoading(false);
      } catch (err) {
        console.error("Chart10 fetch error", err);
        setLoading(false);
      }
    };

    build();
    return () => {
      if (root) root.dispose();
    };
  }, [divRef, supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">
          ABC SKU Segmentation – Last 12 Months
        </Heading>
        <Tooltip
          label="Last-12-month revenue by product. Green (A)=top 20%, Orange (B)=next 30%, Red (C)=remaining 50%."
          hasArrow
          placement="left"
          maxW="240px"
          whiteSpace="normal"
        >
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>
      <div
        ref={setDivRef}
        style={{ width: "100%", height: "calc(100% - 40px)" }}
      />
      {loading && (
        <Spinner
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
        />
      )}
    </Box>
  );
};

export default Chart10;
